import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import { Box, Button, DialogActions, Grid } from "@mui/material";
import Typography from "@mui/material/Typography/Typography";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { Dispatch, useContext, useEffect, useState } from "react";
import { routes } from "schemas/routes";
import { LIGHT_GREY } from "styles/oneOffColor";

import MergeTagChip from "components/Editor/MergeTagWarning/MergeTagChip";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import TemplateBody from "features/Influencer/Templates/SharedComponents/TemplateBody";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";
import { getHighlightedContent } from "utils/string";

interface Props {
  editTemplate: Template;
  setEditTemplate: Dispatch<React.SetStateAction<Template>>;
  followUpTemplate?: Template;
  setFollowUpTemplate?: Dispatch<React.SetStateAction<Template>>;
  handleBack: () => void;
  handleNext: () => void;
  allowChangeTitle?: boolean;
  setIsDirty: Dispatch<React.SetStateAction<boolean>>;
}
const ReviewBentoAiTemplate = ({
  editTemplate,
  setEditTemplate,
  handleBack,
  handleNext,
  followUpTemplate,
  setFollowUpTemplate,
  setIsDirty,
  allowChangeTitle = false,
}: Props) => {
  const { setErrorAlert } = useContext(AlertContext);
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);

  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const isVaOnboarding = isPage(routes.templatesSelfServeOnboarding);
  const [originalHighlightPortion, setOriginalHighlightPortion] = useState("");

  const highlightTemplate = async () => {
    // For AI template that user hasn't made edited to,
    // fetch the highlighted version.
    if (editTemplate?.contentType !== TemplateContentType.OUTREACH) {
      return;
    }
    try {
      setSaveLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/${editTemplate?.id}/highlight`,
        "GET",
      );
      if (res.body) {
        setOriginalHighlightPortion(getHighlightedContent(res.body));
        setEditTemplate((prev) => ({
          ...prev,
          body: res.body,
        }));
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    setIsDirty(false);
    const hasEditedHighlightSentences =
      !originalHighlightPortion ||
      !editTemplate.body?.includes(originalHighlightPortion);

    try {
      setSaveLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/${editTemplate?.id}`,
        "PUT",
        {},
        {
          ...editTemplate,
          validateBulkSendVariables: Boolean(isVaOnboarding),
          hasEditedHighlightSentences,
        },
      );
      setEditTemplate(res.template);

      if (followUpTemplate && followUpTemplate?.id) {
        const { template: followup } = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg?.id}/templates/${followUpTemplate?.id}`,
          "PUT",
          {},
          { ...followUpTemplate },
        );
        if (setFollowUpTemplate) setFollowUpTemplate(followup);
      }
      if (handleNext) {
        handleNext();
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSaveLoading(false);
    }
  };

  useEffect(() => {
    if (editTemplate?.agentSessionId) {
      highlightTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTemplate?.id]);

  if (!editTemplate) {
    return <></>;
  }
  return (
    <Box sx={isPage(routes.onboardingVaSelfServe) ? { p: 3 } : {}}>
      <Typography variant="h4" gutterBottom>
        Review Your Template
      </Typography>
      <Grid container spacing={3} alignItems="flex-start">
        {/* Left column */}
        <Grid item xs={12} md={8}>
          <>
            <Box component="ul" sx={{ pl: 0 }}>
              {isVaOnboarding ? (
                <li style={{ listStyleType: "none" }}>
                  <Typography>
                    🚨 Make sure your templates <strong>do not contain</strong>{" "}
                    any other variables beside{" "}
                    <Box component="span" sx={{ color: "purple.dark" }}>
                      {"{Brand Name}"}
                    </Box>{" "}
                    and{" "}
                    <Box component="span" sx={{ color: "purple.dark" }}>
                      {"{First Name}"}
                    </Box>
                    .
                  </Typography>
                </li>
              ) : (
                <></>
              )}
            </Box>
          </>
          <TemplateBody
            editTemplate={editTemplate}
            setEditTemplate={setEditTemplate}
            isNew={true}
            allowChangeTitle={allowChangeTitle}
            minimalist
            displayError={true}
            setIsDirty={setIsDirty}
          />

          {followUpTemplate && setFollowUpTemplate && (
            <TemplateBody
              editTemplate={followUpTemplate}
              setEditTemplate={setFollowUpTemplate}
              isNew={true}
              allowChangeTitle={allowChangeTitle}
              minimalist
              displayError={true}
              setIsDirty={setIsDirty}
            />
          )}
        </Grid>

        {/* Right column — Suggestions */}

        <Grid item xs={12} md={4} sx={{ order: { xs: -1, md: 1 } }}>
          <Typography variant="h6" gutterBottom>
            Suggestions
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {originalHighlightPortion && (
              <Box
                sx={{
                  backgroundColor: "#fffbea",
                  p: 2,
                  borderRadius: 4,
                  border: "1px solid #ffc937",
                }}
              >
                <Typography>
                  <strong>🌟 Personalize your first sentence </strong>
                </Typography>

                <Typography gutterBottom>
                  Start with a compelling sentence that makes you stand out.
                  Explain why you're reaching out to them specifically, and
                  highlight what makes you a strong fit—whether that's your
                  unique audience, creative angle, or results you've delivered
                  for similar brands. This immediately shows genuine interest
                  and credibility.
                </Typography>

                <Box
                  sx={{ mt: 4, borderLeft: `4px solid ${grey[300]}`, pl: 2 }}
                >
                  Example: <br />
                  <em>
                    I had to reach out-
                    <MergeTagChip mergeTag="{Brand Name}" />
                    is super similar to a recent partnership I did with Kosas,
                    where I created a tutorial (think: big sister vibes) that
                    got 500K+ views (linked here)! I'd love to explore something
                    similar together!
                  </em>
                </Box>
              </Box>
            )}

            <Box
              sx={{
                mt: 4,
                backgroundColor: "#fffbea",
                p: 2,
                borderRadius: 4,
                border: "1px solid #ffc937",
              }}
            >
              <strong>💡 Tip</strong>
              <br />
              Include a link to your top-performing post, portfolio, or social
              profile that showcases strong examples of your work—especially
              branded content. Demonstrating proven results and past
              collaborations significantly increases your reply rate.
            </Box>
          </Box>
        </Grid>
      </Grid>

      <DialogActions>
        <Grid container columnGap={1} justifyContent={"center"}>
          <Button
            sx={{ backgroundColor: LIGHT_GREY, px: 3, py: 1 }}
            disableElevation
            onClick={handleBack}
          >
            <Box
              component={"i"}
              className="fa-regular fa-circle-arrow-left"
              sx={{ mr: 1 }}
            />{" "}
            Back
          </Button>
          <LoadingButton
            disableElevation
            variant="contained"
            type="submit"
            onClick={handleSaveTemplate}
            loading={saveLoading}
            sx={{ px: 3, py: 1 }}
          >
            Next{" "}
          </LoadingButton>
        </Grid>
      </DialogActions>
    </Box>
  );
};

export default ReviewBentoAiTemplate;
